import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Plus, Save, X, Users } from 'lucide-react';
import DistrictMultiSelect from './DistrictMultiSelect';
import DriverFinalLocationPicker from './DriverFinalLocationPicker';

const WAREHOUSE_LOCATION = {
  latitude: -12.000910569563874,
  longitude: -77.053790264342,
};

function DriverSettingsDrawer({
  open,
  onClose,
  driverSettings,
  updateDriver,
  saveDriverPreferences,
  createZone,
  saveZone,
  zones = [],
  districts = [],
}) {
  const drivers = useMemo(() => Object.values(driverSettings), [driverSettings]);
  const selectedCount = drivers.filter((driver) => driver.selected).length;
  const [locationDriver, setLocationDriver] = useState(null);
  const [newZoneName, setNewZoneName] = useState('');
  const [zoneDrafts, setZoneDrafts] = useState({});

  useEffect(() => {
    const drafts = {};
    zones.forEach((zone) => {
      drafts[zone.slug || zone.id] = (zone.districts || []).map((district) => district.name || district);
    });
    setZoneDrafts(drafts);
  }, [zones]);

  if (!open) return null;

  async function saveFinalLocation(driver, final_latitude, final_longitude) {
    const updatedDriver = { ...driver, final_latitude, final_longitude };
    updateDriver(driver.user_id, { final_latitude, final_longitude });
    await saveDriverPreferences(updatedDriver);
    setLocationDriver(null);
  }

  async function setWarehouseStart(driver) {
    const patch = {
      latitude: WAREHOUSE_LOCATION.latitude,
      longitude: WAREHOUSE_LOCATION.longitude,
    };
    const updatedDriver = { ...driver, ...patch };
    updateDriver(driver.user_id, patch);
    await saveDriverPreferences(updatedDriver);
  }

  function toggleDriverZone(driver, zoneSlug) {
    const current = new Set(driver.route_zones || []);
    if (current.has(zoneSlug)) {
      current.delete(zoneSlug);
    } else {
      current.add(zoneSlug);
    }
    const route_zones = Array.from(current);
    updateDriver(driver.user_id, {
      route_zones,
      route_zone: route_zones[0] || '',
    });
  }

  async function submitNewZone() {
    const name = newZoneName.trim();
    if (!name || !createZone) return;
    await createZone({ name, districts: [] });
    setNewZoneName('');
  }

  return (
    <div className="fixed inset-0 z-[2000]">
      <button className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-label="Cerrar" />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-brand-primary" />
            <div>
              <h2 className="font-bold text-slate-900">Conductores disponibles</h2>
              <p className="text-xs text-slate-500">{selectedCount} seleccionados</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg border border-slate-200 p-2 text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <section className="rounded-lg border border-slate-200 p-3">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Zonas operativas</h3>
                <p className="text-xs text-slate-500">Crea zonas y agrega o quita distritos.</p>
              </div>
            </div>
            <div className="mb-3 flex gap-2">
              <input
                value={newZoneName}
                onChange={(event) => setNewZoneName(event.target.value)}
                className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-2 text-sm"
                placeholder="Nueva zona"
              />
              <button
                type="button"
                onClick={submitNewZone}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
              >
                <Plus className="h-3 w-3" />
                Crear
              </button>
            </div>
            <div className="space-y-3">
              {zones.map((zone) => {
                const key = zone.slug || zone.id;
                const selectedDistricts = zoneDrafts[key] || [];
                return (
                  <div key={key} className="rounded-lg bg-slate-50 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-sm font-bold text-slate-800">{zone.name || zone.label}</span>
                      <button
                        type="button"
                        onClick={() => saveZone?.(zone, { districts: selectedDistricts })}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-600"
                      >
                        <Save className="h-3 w-3" />
                        Guardar
                      </button>
                    </div>
                    <DistrictMultiSelect
                      districts={districts}
                      selected={selectedDistricts}
                      onChange={(next) => setZoneDrafts((current) => ({ ...current, [key]: next }))}
                    />
                  </div>
                );
              })}
            </div>
          </section>

          {drivers.map((driver) => (
            <div key={driver.user_id} className="rounded-lg border border-slate-200 p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={driver.selected}
                    onChange={(event) => updateDriver(driver.user_id, { selected: event.target.checked })}
                  />
                  <span className="font-bold text-slate-900">{driver.name}</span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setWarehouseStart(driver)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-bold text-slate-600"
                  >
                    <MapPin className="h-3 w-3" />
                    Base
                  </button>
                  <button
                    onClick={() => setLocationDriver(driver)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-bold text-slate-600"
                  >
                    <MapPin className="h-3 w-3" />
                    Punto final
                  </button>
                  <button
                    onClick={() => saveDriverPreferences(driver)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-bold text-slate-600"
                  >
                    <Save className="h-3 w-3" />
                    Guardar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="text-xs font-semibold text-slate-600">
                  Max paquetes
                  <input
                    type="number"
                    min="1"
                    value={driver.max_packages}
                    onChange={(event) => updateDriver(driver.user_id, { max_packages: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
                  />
                </label>
                <div className="text-xs font-semibold text-slate-600">
                  Zonas
                  <div className="mt-1 grid max-h-32 gap-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                    {zones.map((zone) => {
                      const zoneSlug = zone.slug || zone.id;
                      return (
                        <label key={zoneSlug} className="flex items-center gap-2 text-xs text-slate-700">
                          <input
                            type="checkbox"
                            checked={(driver.route_zones || []).includes(zoneSlug)}
                            onChange={() => toggleDriverZone(driver, zoneSlug)}
                          />
                          <span>{zone.name || zone.label}</span>
                        </label>
                      );
                    })}
                    {!zones.length && <span className="text-xs text-slate-400">Sin zonas creadas</span>}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <div className="mb-2 rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                  Ubicacion actual:{' '}
                  {driver.latitude && driver.longitude
                    ? `${Number(driver.latitude).toFixed(5)}, ${Number(driver.longitude).toFixed(5)}`
                    : 'Sin definir'}
                </div>
                <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                  Punto final:{' '}
                  {driver.final_latitude && driver.final_longitude
                    ? `${Number(driver.final_latitude).toFixed(5)}, ${Number(driver.final_longitude).toFixed(5)}`
                    : 'Sin definir'}
                </div>
                <div className="mb-1 text-xs font-semibold text-slate-600">Distritos preferidos</div>
                <DistrictMultiSelect
                  districts={districts}
                  selected={driver.preferred_districts || []}
                  onChange={(preferred_districts) => updateDriver(driver.user_id, { preferred_districts })}
                />
              </div>
            </div>
          ))}
        </div>
      </aside>
      <DriverFinalLocationPicker
        driver={locationDriver}
        onClose={() => setLocationDriver(null)}
        onSave={(lat, lng) => saveFinalLocation(locationDriver, lat, lng)}
      />
    </div>
  );
}

export default DriverSettingsDrawer;
